import {
  ability,
  ACTIONS,
  ROLES,
  SUBJECTS,
  SubjectWithAttributes
} from '../../../../../../Permission'
import { Button, BUTTON_TYPES } from '../../../../../../../components/Button'
import { ReactComponent as Plus16 } from '../../../../../../../assets/svg/Plus16.svg'
import {
  BTN_TXT,
  PLACEHOLDERS,
  USER_STATUSES
} from '../../../../../../../constants'
import { Table } from '../../../../../../../components/Table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { columns, USERS_ROLES_TABS } from './constants'
import {
  TUsersTable,
  TUsersTableFilters,
  TUsersTableFiltersFunc,
  TUsersTableParams,
  TUsersTableRequestData
} from './types'

import { DEFAULT_PAGE } from '../../../../../../../components/Table/constants'
import { setLoading } from '../../../../../../../redux/store/common/slice'
import { fullFormatPhoneNumber } from '../../../../../../../helper/common'
import { useDispatch, useSelector } from 'react-redux'
import { getUsers } from './api'
import useRouter from '../../../../../../../hooks/useRouter'
import { useDebounce } from '../../../../../../../hooks/hooks'
import { CancelTokenSource } from 'axios'
import { Input } from '../../../../../../../components/Input'
import { ReactComponent as Close16 } from '../../../../../../../assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from '../../../../../../../assets/svg/Search16.svg'
import './../../styles.scss'
import { UserArchivePopup } from '../../../../../../../pages/CRUDUser/UserArchivePopup'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { routes } from '../../../../../../../router'
import { getUser } from '../../../../../../../redux/store/user/getters'
import { UserRolesFilterComponent } from '../../../../../../../components/UserRolesFilterComponent/UserRolesFilterComponent'
import {
  Typography,
  TYPOGRAPHY_WEIGHT
} from '../../../../../../../components/Typography'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { subject } from '@casl/ability'

export const UsersTable = () => {
  const {
    query: { hospitalId }
  } = useRouter()

  const [searchValue, setSearchValue] = useState('')
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [selectedByActionsUser, setSelectedByActionsUser] =
    useState<TUsersTable | null>(null)
  const [firstLoad, setFirstLoad] = useState(true)
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [sortParams, setSortParams] = useState<TUsersTableRequestData>({})
  const [tableData, setTableData] = useState<TUsersTable[]>([])
  const [filters, setFilters] = useState<TUsersTableFilters>({})
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<TUsersTable[]>([])

  const debouncedSearch = useDebounce(searchValue, 500)
  const { push } = useRouter()

  const user = useSelector(getUser)

  const hsId =
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.CONTRACT_STEWARD ||
    user.role === ROLES.HOSPITAL_ADMIN
      ? user.health_system
      : null

  const canManageUsers = ability.can(
    ACTIONS.CRUD,
    subject(SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS, {
      uuid: user.role === ROLES.HOSPITAL_ADMIN ? hospitalId : user.health_system
    } as unknown as SubjectWithAttributes)
  )

  const canArchiveUsers = ability.can(
    ACTIONS.ARCHIVE,
    subject(SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS, {
      uuid: user.health_system
    } as unknown as SubjectWithAttributes)
  )

  const canCrudUsersInHSDetails = ability.can(
    ACTIONS.CRUD,
    subject(SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS, {
      uuid: user.health_system
    } as unknown as SubjectWithAttributes)
  )

  const dispatch = useDispatch()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab])

  const getTableData = useCallback(
    ({ sortField, sortOrder, filters, page }: TUsersTableRequestData) => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }

      dispatch(setLoading(true))
      const params: TUsersTableParams = { ...filters }

      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      getUsers(hospitalId, activeTab, params)
        .then((resp) => {
          if (!resp.data?.results) {
            return
          }
          setTableData(
            resp.data.results.map((i, index) => ({
              ...i,
              phone_number: fullFormatPhoneNumber(i.phone_number),
              key: `${i.uuid}_${index}`
            }))
          )
          setTotalNumber(resp.data.count)
          if (firstLoad) {
            setFirstLoad(false)
          }
        })
        .finally(() => dispatch(setLoading(false)))
    },
    [
      tableDataTokenRef,
      firstLoad,
      hospitalId,
      activeTab,
      searchValue,
      dispatch,
      pageInfo
    ]
  )

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const applyFilters: TUsersTableFiltersFunc = useCallback(
    (field, appliedFilters) => {
      const newFilters = { ...filters, [field]: appliedFilters }
      setFilters(newFilters)
      getTableData({ ...sortParams, filters: newFilters })
    },
    [filters, sortParams, getTableData]
  )

  const onArchive = useCallback((user) => {
    setIsArchiveOpen(true)
    setSelectedByActionsUser(user)
  }, [])

  const tableColumns = useMemo(
    () =>
      columns(
        filters,
        applyFilters,
        activeTab,
        hospitalId,
        onArchive,
        canArchiveUsers,
        canCrudUsersInHSDetails
      ),
    [
      filters,
      applyFilters,
      activeTab,
      hospitalId,
      onArchive,
      canArchiveUsers,
      canCrudUsersInHSDetails
    ]
  )

  const onChangeRole = (e) => {
    setActiveTab(e)
    setSortParams({})
    onCancelSelection()
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  const onEditUser = useCallback(() => {
    push(`${routes.createUserProfile}/${selectedRowKeys[0]}?fromCommunity=true`)
  }, [push, selectedRowKeys])

  const onCreateUser = useCallback(() => {
    if (hsId)
      push(
        `${routes.createUserProfile}/new?hsUser=${hsId}&fromCommunity=true${
          hospitalId ? `&hsHosUser=${hospitalId}` : ''
        }`,
        {
          state: { selectedHs: hsId, selectedHos: hospitalId }
        }
      )
    else {
      push(`${routes.createUserProfile}/new?fromCommunity=true`)
    }
  }, [push, hsId, hospitalId])

  const onCancelSelection = useCallback(() => {
    setIsArchiveOpen(false)
    setSelectedByActionsUser(null)
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  return (
    <div className="column gap-16">
      <div className="row space-between">
        <div className="row gap-8 align-center">
          <UserRolesFilterComponent
            roles={USERS_ROLES_TABS}
            onChange={onChangeRole}
          />
          {!!selectedRowKeys.length ? (
            <Typography.Body1
              className="table-wrapper__select-title"
              weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
            >
              Selected {selectedRowKeys.length} of {totalNumber}
            </Typography.Body1>
          ) : (
            <Input
              className="allow-clear users-table__search"
              propsInput={{
                allowClear: { clearIcon: <Close16 /> },
                placeholder: PLACEHOLDERS.SEARCH_BY_NAME_AND_EMAIL,
                prefix: <Search16 />,
                value: searchValue,
                onChange: (e) => setSearchValue(e.target.value)
              }}
            />
          )}
        </div>
        {canManageUsers && (
          <div className="row gap-8">
            {!selectedRowKeys.length && (
              <div className="row justify-end">
                <Button
                  type={BUTTON_TYPES.PRIMARY}
                  icon={<Plus16 />}
                  upperCase
                  onClick={onCreateUser}
                >
                  {BTN_TXT.CREATE_USER}
                </Button>
              </div>
            )}
            {selectedRowKeys.length === 1 && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Edit16 />}
                upperCase
                onClick={onEditUser}
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {((!!selectedRows.length &&
              canArchiveUsers &&
              selectedRows.filter((i) => i.status !== USER_STATUSES.ARCHIVED)
                .length === selectedRows.length) ||
              isArchiveOpen) && (
              <UserArchivePopup<TUsersTable>
                selected={
                  isArchiveOpen && selectedByActionsUser
                    ? [selectedByActionsUser]
                    : selectedRows
                }
                callback={() => {
                  onCancelSelection()
                  getTableData({ ...sortParams, ...filters })
                }}
                isTable
                isOpenFromTheOutside={isArchiveOpen}
                setIsOpenFromTheOutside={setIsArchiveOpen}
              />
            )}
          </div>
        )}
      </div>
      <Table<TUsersTable>
        dataSource={tableData}
        className="hospitals-list-table"
        columns={tableColumns}
        onChange={handleTableChange}
        onChangePage={handleChangePageSize}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        createButton={
          canManageUsers
            ? {
                buttonText: BTN_TXT.CREATE_USER,
                action: onCreateUser,
                availability: true
              }
            : undefined
        }
        rowSelection={
          canManageUsers
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                getCheckboxProps: (record) => ({
                  disabled:
                    record.uuid === user.uuid ||
                    ('can_be_managed' in record && !record.can_be_managed)
                }),
                renderCell: (_checked, record, _, node) => {
                  if ('can_be_managed' in record && !record.can_be_managed) {
                    return (
                      <Tooltip
                        title={
                          'You do not have permission to manage users of this role'
                        }
                        overlayInnerStyle={{
                          display: 'block',
                          width: 264
                        }}
                        placement="topLeft"
                      >
                        {node}
                      </Tooltip>
                    )
                  }
                  return node
                }
              }
            : undefined
        }
      />
    </div>
  )
}
